import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Dialog,
  Button,
  MenuItem,
  useMediaQuery,
  Autocomplete,
  Checkbox,
  Chip,
  Link,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import {
  useUpdateUserPrivilegesMutation,
  useGetUserRoleQuery,
  useGetTeamQuery,
  useGetallLocationQuery,
} from "../../../Api/attoDeskApi";
import CloseIcon from "@mui/icons-material/Close";
import {
  validateUsername,
  validateUserEmail,
} from "../../../Components/Validation/validationUtils";
import { useNavigate } from "react-router-dom";
import {
  IUserRole,
  ITeam,
  CustomerOption,
  IUserAdmin,
  IStudentLocation,
} from "../../../Api/Interface/api.interface";
import NewUserRole from "../../UserRole/Edit/index";
import AddIcon from "@mui/icons-material/Add";
import { capitalizeFirstLetter } from "../../../Components/Validation/capitalizeFirstLetter";
import useIsMobile from "../../../Core/hook/useIsMobile";

interface FormValues {
  id: string;
  username: string;
  email: string;
  user_role: string;
  staffId: string;
}

interface IUserAdminPopUp {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data: IUserAdmin;
}

const UserAdmin = ({
  openModel = false,
  handleCloseDialog,
  data,
}: IUserAdminPopUp) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobileRes = useIsMobile();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useUpdateUserPrivilegesMutation();
  const [openDepartment, setOpenDepartment] = useState(false);
  // A user may be assigned to many Campus Locations.
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  useEffect(() => {
    if (Array.isArray(data.location_ids) && data.location_ids.length) {
      setSelectedLocationIds(data.location_ids.map(Number).filter(Boolean));
      return;
    }
    const raw = data.location_id;
    if (raw === null || raw === undefined || raw === "") {
      setSelectedLocationIds([]);
      return;
    }
    if (typeof raw === "string" && raw.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setSelectedLocationIds(parsed.map(Number).filter(Boolean));
          return;
        }
      } catch {
        /* fall through to scalar handling */
      }
    }
    const num = Number(raw);
    setSelectedLocationIds(num > 0 ? [num] : []);
  }, [data]);

  const [open] = React.useState(openModel);
  const handleClose = () => {
    handleCloseDialog(false);
  };

  // The permanent protected system account: its role and username are immutable
  // (enforced again on the backend). Hide the role picker and lock the username.
  // Username and email are separate; identify it by either.
  const isProtected =
    (data.username || "").toLowerCase() === "vmadmin" ||
    (data.email || "").toLowerCase() === "vmadmin@gmail.com";

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetUserRoleQuery({
      searchText: "",
    });

  const { data: staffData, isLoading: staffIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const { data: locationData, isLoading: locationIsLoading } =
    useGetallLocationQuery({ searchText: "" });

  const locationOptions = useMemo(() => {
    const list = (locationData?.data as IStudentLocation[]) || [];
    if (!Array.isArray(list)) return [];
    return list.map((loc) => ({
      label: loc.location_name,
      value: Number(loc.id),
    }));
  }, [locationData?.data]);

  const userroleList = useMemo(() => {
    return userroleData?.data as IUserRole[];
  }, [userroleData?.data]);

  const staffList = useMemo(() => {
    return staffData?.data as ITeam[];
  }, [staffData?.data]);

  const staffOptions = useMemo(() => {
    if (!Array.isArray(staffList)) return [];
    return staffList.map((cust) => ({
      label: `${capitalizeFirstLetter(
        cust.teamFirstName
      )} ${capitalizeFirstLetter(cust.teamLastName)}`,
      value: cust.id,
    }));
  }, [staffList]);

  const userRoleOptions = useMemo(() => {
    if (!Array.isArray(userroleList)) return [];
    return userroleList.map((cust) => ({
      label: `${capitalizeFirstLetter(cust.roleName)}`,
      value: Number(cust.id),
    }));
  }, [userroleList]);

  // Super Admin detection — same rule as Create User: the role carries
  // "System_Admin" among its allPrivileges. Backend re-verifies.
  const superAdminRoleIds = useMemo(() => {
    if (!Array.isArray(userroleList)) return new Set<number>();
    const s = new Set<number>();
    (userroleList as any[]).forEach((r) => {
      const privs: string[] = Array.isArray(r?.allPrivileges) ? r.allPrivileges : [];
      if (privs.includes("System_Admin")) s.add(Number(r.id));
    });
    return s;
  }, [userroleList]);

  const formik = useFormik<FormValues>({
    initialValues: {
      id: data.id,
      username: data.username,
      email: data.email || "",
      user_role: data.user_role,
      staffId: data.staffId,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const usernameError = validateUsername(values.username);
      if (usernameError) {
        errors.username = usernameError;
      }

      const emailError = validateUserEmail(values.email);
      if (emailError) {
        errors.email = emailError;
      }

      const roleIsSuperAdmin = superAdminRoleIds.has(Number(values.user_role));
      if (values.user_role && !roleIsSuperAdmin && selectedLocationIds.length === 0) {
        errors.location_ids = "Assign at least one campus location.";
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const roleIsSuperAdmin = superAdminRoleIds.has(Number(values.user_role));
        const updatedData = {
          id: values.id,
          username: values.username,
          email: values.email,
          user_role: values.user_role,
          staffId: values.staffId,
          location_ids: roleIsSuperAdmin ? [] : selectedLocationIds,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const allLocationsSelected =
    locationOptions.length > 0 &&
    selectedLocationIds.length === locationOptions.length;

  const toggleSelectAllLocations = () => {
    setSelectedLocationIds(
      allLocationsSelected ? [] : locationOptions.map((o) => o.value)
    );
  };

  const formValid = useMemo(() => {
    return (
      formik.values.username !== "" &&
      formik.values.username !== undefined &&
      formik.values.email !== "" &&
      formik.values.email !== undefined &&
      formik.values.user_role !== "" &&
      formik.values.user_role !== undefined
      // formik.values.staffId !== "" &&
      // formik.values.staffId !== undefined &&
      // selectedCategories.length > 0
    );
  }, [
    formik.values.username,
    formik.values.email,
    formik.values.user_role,
    // formik.values.staffId,
    // selectedCategories,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile :classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                    {data ? "Edit" : "Add"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            User Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                User Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="User Name"
                                size="small"
                                disabled={isProtected}
                                {...formik.getFieldProps("username")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.username &&
                                  Boolean(formik.errors.username)
                                }
                                helperText={
                                  formik.touched.username &&
                                  formik.errors.username
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Email Address{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Email Address"
                                size="small"
                                {...formik.getFieldProps("email")}
                                className={classes.textField}
                                InputProps={{ sx: { fontSize: 14 } }}
                                InputLabelProps={{ sx: { fontSize: 14 } }}
                                error={
                                  formik.touched.email &&
                                  Boolean(formik.errors.email)
                                }
                                helperText={
                                  formik.touched.email && formik.errors.email
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Staff{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Autocomplete
                                options={staffOptions}
                                loading={staffIsLoading}
                                getOptionLabel={(option: CustomerOption) =>
                                  option.label
                                }
                                value={
                                  staffOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      Number(formik.values.staffId)
                                  ) || null
                                }
                                onChange={(
                                  _,
                                  newValue: CustomerOption | null
                                ) => {
                                  formik.setFieldValue(
                                    "staffId",
                                    newValue?.value || ""
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Staff"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    className="textField"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {staffIsLoading ? (
                                            <CircularProgress size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid> */}

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                User Role{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            {isProtected ? (
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Chip
                                  label="ðŸ›¡ SUPER ADMIN"
                                  sx={{
                                    bgcolor: "#FEF3C7",
                                    color: "#92400E",
                                    fontWeight: 700,
                                    border: "1px solid #FCD34D",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", mt: 1, color: "#92400E" }}
                                >
                                  This is a protected system account. Its role
                                  cannot be changed.
                                </Typography>
                              </Grid>
                            ) : (
                              <>
                            <Grid item lg={10.5} md={10.5} sm={10.5} xs={10.5}>
                              <Autocomplete
                                options={userRoleOptions}
                                loading={userroleIsLoading}
                                getOptionLabel={(option: CustomerOption) =>
                                  option.label
                                }
                                value={
                                  userRoleOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      Number(formik.values.user_role)
                                  ) || null
                                }
                                onChange={(
                                  _,
                                  newValue: CustomerOption | null
                                ) => {
                                  formik.setFieldValue(
                                    "user_role",
                                    newValue?.value || ""
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="User Role"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    className="textField"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {userroleIsLoading ? (
                                            <CircularProgress size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                            <Grid
                              item
                              lg={1.5}
                              md={1.5}
                              sm={1.5}
                              xs={1.5}
                              display="flex"
                              sx={{
                                justifyContent: "flex-end",
                                alignItems: "flex-end",
                                textAlign: "end",
                              }}
                            >
                              <Button
                                variant="contained"
                                className={classes.editButton}
                                onClick={() => {
                                  setOpenDepartment(true);
                                }}
                              >
                                <AddIcon />
                              </Button>
                            </Grid>
                              </>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  {/* Campus Location card — hidden for Super Admin (same
                      rule as Create User). Backend clears any stored
                      list on save when the role is Super Admin. */}
                  {!superAdminRoleIds.has(Number(formik.values.user_role)) && (
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Campus Location
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="subtitle1">
                                Assigned Locations
                              </Typography>
                              {locationOptions.length > 0 && (
                                <Link
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  onClick={toggleSelectAllLocations}
                                  sx={{ fontSize: 12.5 }}
                                >
                                  {allLocationsSelected
                                    ? "Clear All"
                                    : "Select All"}
                                </Link>
                              )}
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Autocomplete
                                multiple
                                options={locationOptions}
                                loading={locationIsLoading}
                                disableCloseOnSelect
                                getOptionLabel={(option: CustomerOption) =>
                                  option.label
                                }
                                value={locationOptions.filter((o) =>
                                  selectedLocationIds.includes(o.value)
                                )}
                                onChange={(_, v) =>
                                  setSelectedLocationIds(
                                    v.map((item: any) => item.value)
                                  )
                                }
                                renderOption={(props, option, { selected }) => (
                                  <li {...props}>
                                    <Checkbox
                                      size="small"
                                      checked={selected}
                                      sx={{ mr: 1 }}
                                    />
                                    {option.label}
                                  </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                  value.map((option: any, index: number) => (
                                    <Chip
                                      {...getTagProps({ index })}
                                      key={option.value}
                                      label={option.label}
                                      size="small"
                                      sx={{
                                        borderRadius: "8px",
                                        fontSize: 11.5,
                                        height: 24,
                                      }}
                                    />
                                  ))
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={
                                      selectedLocationIds.length
                                        ? ""
                                        : "Select locations"
                                    }
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    className="textField"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {locationIsLoading ? (
                                            <CircularProgress size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  )}
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid || isLoading}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
                {openDepartment && (
                  <NewUserRole
                    handleCloseDialog={() => setOpenDepartment(false)}
                    openModel={openDepartment}
                  />
                )}
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default UserAdmin;
